import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * NEXORA Ocean Design System (NODS)
 * Button Component
 * 
 * Based on design_guidelines.json:
 * - Modern, sharp or rounded-lg (max 8px)
 * - Active state scales down (scale-95)
 * - 200-300ms transitions
 * - No universal transitions
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/20 active:scale-95',
  {
    variants: {
      variant: {
        primary: 'bg-primary hover:bg-primary-hover text-primary-foreground shadow-sm',
        secondary: 'bg-secondary hover:bg-secondary/80 text-secondary-foreground',
        destructive: 'bg-error hover:bg-error/90 text-white',
        outline: 'border border-border bg-transparent hover:bg-surface-elevated text-text-primary',
        ghost: 'hover:bg-surface-elevated text-text-primary',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        default: 'h-10 px-4 py-2',
        lg: 'h-11 px-6 text-lg',
        icon: 'h-10 w-10'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
);

// Export buttonVariants for use in other components like alert-dialog
export { buttonVariants };

export const Button = React.forwardRef((
  { className, variant, size, children, ...props },
  ref
) => {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {children}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
